import { UserStatusType } from "gql/graphql";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from "react";

import { useAuth } from "../auth-provider";

export interface IUserPresence {
  isUserOnline: (userId: string) => boolean;
  getUserStatusType: (userId: string) => UserStatusType;
  setUsersOnlineStatus: (userIds: string[], statusType: UserStatusType) => void;
}

const UserPresenceContext = createContext<IUserPresence | null>(null);

export interface IProviderProps {
  children?: ReactNode;
}

/**
 * In-memory org user online/offline state. Updated by Socket.IO
 * `online_users` (full org snapshot) and by `USERS_ONLINE` websocket data payloads.
 */
export const UserPresenceProvider: FC<IProviderProps> = ({ children }) => {
  const { isLoggedIn } = useAuth();
  const [onlineUserIdSet, setOnlineUserIdSet] = useState<Set<string>>(
    () => new Set(),
  );

  useEffect(() => {
    if (!isLoggedIn) {
      setOnlineUserIdSet(new Set());
    }
  }, [isLoggedIn]);

  const setUsersOnlineStatus = useCallback(
    (userIds: string[], statusType: UserStatusType) => {
      setOnlineUserIdSet((prev) => {
        const next = new Set(prev);
        for (const id of userIds) {
          if (statusType === UserStatusType.Online) {
            next.add(id);
          } else {
            next.delete(id);
          }
        }
        return next;
      });
    },
    [],
  );

  const isUserOnline = useCallback(
    (userId: string) => onlineUserIdSet.has(userId),
    [onlineUserIdSet],
  );

  const getUserStatusType = useCallback(
    (userId: string) =>
      onlineUserIdSet.has(userId) ? UserStatusType.Online : UserStatusType.Offline,
    [onlineUserIdSet],
  );

  const value = useMemo<IUserPresence>(
    () => ({
      isUserOnline,
      getUserStatusType,
      setUsersOnlineStatus,
    }),
    [getUserStatusType, isUserOnline, setUsersOnlineStatus],
  );

  return (
    <UserPresenceContext.Provider value={value}>
      {children}
    </UserPresenceContext.Provider>
  );
};

export const useUserPresence = () => {
  const context = useContext(UserPresenceContext);
  if (!context) {
    throw new Error("useUserPresence must be used within a UserPresenceProvider");
  }
  return context;
};
